import {useRef, useEffect, useState, useCallback, useMemo} from 'react'
import {Button} from '@primer/react'
import {clsx} from 'clsx'
import safeStorage from '@github-ui/safe-storage'
import styles from './ExpandableContent.module.css'
import {usePullRequestAnalytics} from '../../hooks/use-pull-request-analytics'

const DEFAULT_MAX_HEIGHT = 256
const storage = safeStorage('localStorage')

interface ExpandableContentProps {
  /** Content rendered inside the expandable wrapper */
  children: React.ReactNode
  /** Additional class name applied to the outer wrapper */
  className?: string
  /** Section name to add to show more label for a11y */
  sectionLabel: string
  /** Max height in pixels before content is collapsed (default: 256) */
  maxHeight?: number
  /** localStorage key to persist expand/collapse state. When omitted, state is not persisted. */
  storageKey?: string
  /** Initial expanded state used on first view when no persisted preference exists (default: false) */
  defaultExpanded?: boolean
}

/** Wraps content with overflow detection, gradient fade, and "Show more" toggle */
export function ExpandableContent({
  children,
  sectionLabel,
  className,
  maxHeight = DEFAULT_MAX_HEIGHT,
  storageKey,
  defaultExpanded = false,
}: ExpandableContentProps) {
  const bodyRef = useRef<HTMLDivElement>(null)
  const [contentHeight, setContentHeight] = useState(0)
  const [userPreference, setUserPreference] = useState<boolean | null>(null)

  const isExpanded = useMemo(() => {
    if (userPreference !== null) return userPreference
    if (storageKey) {
      const stored = storage.getItem(storageKey)
      if (stored !== null) return stored === 'true'
    }
    return defaultExpanded
  }, [userPreference, storageKey, defaultExpanded])

  // Coalesce every height measurement into a single rAF and bail when the value
  // is unchanged. Writing contentHeight synchronously from the ResizeObserver
  // (and again from the isExpanded effect) produced two paints per expand on
  // Safari and could re-trigger the observer mid-transition.
  const rafIdRef = useRef(0)
  const measureContentHeight = useCallback(() => {
    const node = bodyRef.current
    if (!node || rafIdRef.current) return
    rafIdRef.current = requestAnimationFrame(() => {
      rafIdRef.current = 0
      const next = node.scrollHeight
      setContentHeight(prev => (prev === next ? prev : next))
    })
  }, [])

  useEffect(() => {
    const node = bodyRef.current
    if (!node) return

    measureContentHeight()

    const resizeObserver = new ResizeObserver(measureContentHeight)
    resizeObserver.observe(node)

    return () => {
      resizeObserver.disconnect()
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current)
        rafIdRef.current = 0
      }
    }
  }, [measureContentHeight])

  useEffect(() => {
    measureContentHeight()
  }, [isExpanded, measureContentHeight])

  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const handleToggle = useCallback(() => {
    const next = !isExpanded
    sendPullRequestAnalyticsEvent('overview.expandable_content_toggle', 'OVERVIEW_EXPANDABLE_CONTENT', {
      section_label: sectionLabel,
      is_expanded: next,
    })
    setUserPreference(next)
    if (storageKey) {
      storage.setItem(storageKey, JSON.stringify(next))
    }
  }, [isExpanded, sectionLabel, sendPullRequestAnalyticsEvent, storageKey])

  const isCollapsible = contentHeight > maxHeight
  const isCollapsed = isCollapsible && !isExpanded

  return (
    <div className={clsx(styles.container, className)} data-testid="expandable-content-container">
      {/* The toggle button is the focus target for this interaction. */}
      <div
        className={clsx(styles.content, isCollapsed && styles.collapsed)}
        data-testid="expandable-content-body"
        onClick={isCollapsed ? handleToggle : undefined}
        // When collapsed, hide the content from screen readers - otherwise they'll still encounter it and expanding/collapsing
        // would have no effect except to make it noninteractive, which would just be frustrating.
        aria-hidden={isCollapsed ? true : undefined}
        style={isCollapsed ? {maxHeight: `${maxHeight}px`} : undefined}
        ref={bodyRef}
      >
        <div
          inert={isCollapsed ? true : undefined}
          className={styles.inertWrapper}
          data-testid="expandable-content-inert"
        >
          {children}
        </div>
      </div>
      {isCollapsible && (
        <div className={clsx(styles.toggle, isCollapsed && styles.toggleFloating)}>
          <Button
            variant="default"
            size="medium"
            onClick={handleToggle}
            aria-expanded={isExpanded}
            // For screen reader users, the interaction is a bit different because they don't encounter the preview of
            // the content - it's either fully hidden or fully shown
            aria-label={`${isExpanded ? 'Show less' : 'Show more'} ${sectionLabel}`}
          >
            {isExpanded ? 'Show less' : 'Show more'}
          </Button>
        </div>
      )}
    </div>
  )
}
